"use client";

import React from "react";
import NextImage from "@/components/common/NextImage";
import { HEADER_V2_DESIGN_SYSTEM } from "@/components/header/v2/designTokens";

interface PromoCardV2Props {
  imageUrl: string;
  mobileUrl?: string;
  navigationLink: string;
  title?: string;
  description?: string;
  ctaText?: string;
  className?: string;
}

const PromoCardV2: React.FC<PromoCardV2Props> = ({
  imageUrl,
  mobileUrl,
  navigationLink,
  title,
  description,
  ctaText,
  className = "",
}) => {
  const displayMobileUrl = mobileUrl?.trim() || imageUrl;

  return (
    <div
      className={`relative w-full h-full overflow-hidden ${HEADER_V2_DESIGN_SYSTEM.borderRadius.card} ${HEADER_V2_DESIGN_SYSTEM.elevation.card} ${HEADER_V2_DESIGN_SYSTEM.elevation.cardHover} transition-all duration-300 ${className}`}
    >
      <a
        href={navigationLink}
        className="block w-full h-full relative group outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#D97706] rounded-xl"
        tabIndex={0}
        aria-label={title || "Promotional Banner"}
      >
        {/* Image Display */}
        <div className="absolute inset-0 w-full h-full">
          {/* Desktop Image */}
          <div className="hidden md:block relative w-full h-full">
            <NextImage
              src={imageUrl}
              alt={title || "Promotional Spotlight"}
              fill
              sizes="(max-width: 1024px) 100vw, 30vw"
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.01]"
            />
          </div>
          {/* Mobile Image */}
          <div className="block md:hidden relative w-full h-full">
            <NextImage
              src={displayMobileUrl}
              alt={title || "Promotional Spotlight"}
              fill
              sizes="100vw"
              className="object-cover transition-transform duration-700 ease-out"
            />
          </div>
        </div>

        {/* Text and Button Overlay (Only rendered if title/description is explicitly provided) */}
        {(title || description || ctaText) && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6 text-white transition-opacity duration-300">
            <div className="space-y-2">
              {title && (
                <h3 className={`font-semibold tracking-tight text-white ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilyHeading} text-lg md:text-xl`}>
                  {title}
                </h3>
              )}
              {description && (
                <p className={`text-xs md:text-sm text-gray-200 line-clamp-2 ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilySans}`}>
                  {description}
                </p>
              )}
              {ctaText && (
                <div className="pt-2">
                  <span className={`inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#D97706] border-b-2 border-transparent hover:border-[#D97706] transition-colors duration-200`}>
                    {ctaText}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subtle overlay transition on card hover when there's no text overlay */}
        {!(title || description || ctaText) && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 pointer-events-none" />
        )}
      </a>
    </div>
  );
};

export default React.memo(PromoCardV2);
